import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../services/apiClient';
import StakeTable from './StakeTable';
import { Button, Badge, Modal, FormInput, Toggle } from '../../components/ui';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';

interface Provider {
  id: number;
  aggregator_id: number;
  aggregator_name?: string;
  name: string;
  provider_code: string;
  developer_is_enabled: boolean;
  admin_is_enabled: boolean;
  api_is_enabled: boolean;
  supported_currencies?: string[];
}

const fetchProvidersAPI = async (aggregatorId?: number | '', showAll?: boolean): Promise<Provider[]> => {
  const token = localStorage.getItem("token");
  let url = `/api/game-engine/admin/providers`;
  const params: string[] = [];
  if (aggregatorId) params.push(`aggregator_id=${aggregatorId}`);
  if (showAll) params.push(`show_all=true`);
  if (params.length) url += `?${params.join('&')}`;
  const res = await apiClient.get(url, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return res.data.success ? res.data.data : [];
};

const fetchAggregatorsAPI = async () => {
  const token = localStorage.getItem("token");
  const res = await apiClient.get(`/api/game-engine/admin/aggregators`, { headers: { Authorization: `Bearer ${token}` } });
  return res.data.success ? res.data.data : [];
};

const fetchCurrenciesAPI = async (): Promise<any[]> => {
  const token = localStorage.getItem("token");
  const res = await apiClient.get(`/api/currencies`, { headers: { Authorization: `Bearer ${token}` } });
  return res.data.success ? res.data.currencies : [];
};

const fetchGroupsAPI = async () => {
  const token = localStorage.getItem('token');
  const res = await apiClient.get('/api/game-engine/admin/groups', { headers: { Authorization: `Bearer ${token}` } });
  return res.data.success ? res.data.data : [];
};

const Providers: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [limit, setLimit] = useState(50);
  const [selectedAggregatorId, setSelectedAggregatorId] = useState<number | ''>('');

  const { data: providers = [], isLoading } = useQuery({ 
    queryKey: ['providers', selectedAggregatorId], 
    queryFn: () => fetchProvidersAPI(selectedAggregatorId) 
  });
  const { data: aggregators = [] } = useQuery({ queryKey: ['aggregators'], queryFn: fetchAggregatorsAPI });
  const { data: allActiveCurrencies = [] } = useQuery({ queryKey: ['currencies'], queryFn: fetchCurrenciesAPI });
  const { data: groups = [] } = useQuery({ queryKey: ['groups'], queryFn: fetchGroupsAPI });

    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [selectedAssignGroupId, setSelectedAssignGroupId] = useState<number | ''>('');

    const assignToGroupMutation = useMutation({
      mutationFn: async () => {
        if (!selectedAssignGroupId) throw new Error('No group selected');
        const token = localStorage.getItem('token');
        return apiClient.post(`/api/game-engine/admin/groups/${selectedAssignGroupId}/assign-games`, { provider_ids: selectedIds }, { headers: { Authorization: `Bearer ${token}` } });
      },
      onSuccess: (res) => {
        toast.success(res.data.message || 'Providers successfully assigned to group!');
        setSelectedIds([]);
        setSelectedAssignGroupId('');
      },
      onError: () => toast.error('Failed to assign providers to group')
    });
  
  // Currency Modal State
  const [isCurrencyModalOpen, setIsCurrencyModalOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [availableCurrencies, setAvailableCurrencies] = useState<string[]>([]);
  const [providerCurrencies, setProviderCurrencies] = useState<string[]>([]);

  // Password Protection State
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [pendingAction, setPendingAction] = useState<() => void>(() => {});

  const requirePassword = (action: () => void) => {
    setPendingAction(() => action);
    setPasswordInput('');
    setIsPasswordModalOpen(true);
  };

  const verifyPassword = () => {
    if (passwordInput === 'Metablock@123##') {
      setIsPasswordModalOpen(false);
      pendingAction();
    } else {
      toast.error('Incorrect password');
    }
  };

  const saveMutation = useMutation({
    mutationFn: async (data: Partial<Provider>) => {
      const token = localStorage.getItem("token");
      if (data.id) return apiClient.put(`/api/game-engine/admin/providers/${data.id}`, data, { headers: { Authorization: `Bearer ${token}` } });
      return apiClient.post(`/api/game-engine/admin/providers`, data, { headers: { Authorization: `Bearer ${token}` } });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['providers'] });
      // Only toast if it's not a bulk update (bulk update has its own toast)
      if (!(variables as any)._isToggle) {
         toast.success(variables.id ? "Provider updated!" : "Provider created!");
      }
    },
    onError: () => toast.error("Failed to save provider")
  });

  
  
  const [isBulkDevModalOpen, setIsBulkDevModalOpen] = useState(false);
  const [bulkDevAggId, setBulkDevAggId] = useState<number | ''>('');
  const [bulkDevState, setBulkDevState] = useState<Record<number, boolean>>({});
  const [isBulkDevSaving, setIsBulkDevSaving] = useState(false);
  const [bulkDevProviders, setBulkDevProviders] = useState<Provider[]>([]);

  const openBulkDevStatusModal = () => {
    requirePassword(() => {
      setBulkDevAggId('');
      setBulkDevState({});
      setBulkDevProviders([]);
      setIsBulkDevModalOpen(true);
    });
  };

  const handleDevAggregatorSelect = async (aggId: number) => {
    setBulkDevAggId(aggId);
    setBulkDevProviders([]);
    setBulkDevState({});
    try {
      const aggProviders = await fetchProvidersAPI(aggId, true);
      setBulkDevProviders(aggProviders);
      const newState: Record<number, boolean> = {};
      aggProviders.forEach((p: any) => {
        newState[p.id] = p.developer_is_enabled;
      });
      setBulkDevState(newState);
    } catch (e) {
      toast.error('Failed to load providers for selected aggregator');
    }
  };

  const handleBulkDevToggle = (providerId: number, checked: boolean) => {
    setBulkDevState(prev => ({
      ...prev,
      [providerId]: checked
    }));
  };

  const handleDevGlobalToggle = (checked: boolean) => {
    setBulkDevState(prev => {
      const newState = { ...prev };
      bulkDevProviders.forEach((p: any) => {
        newState[p.id] = checked;
      });
      return newState;
    });
  };

  const saveBulkDevStatus = async () => {
    if (!bulkDevAggId) return;
    setIsBulkDevSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      const promises = bulkDevProviders.map((p: any) => {
         const newStatus = bulkDevState[p.id] !== undefined ? bulkDevState[p.id] : p.developer_is_enabled;
         if (newStatus !== p.developer_is_enabled) {
            return apiClient.put(`/api/game-engine/admin/providers/${p.id}`, { ...p, developer_is_enabled: newStatus, _isToggle: true }, { headers: { Authorization: `Bearer ${token}` } });
         }
         return Promise.resolve();
      });
      
      await Promise.all(promises);
      queryClient.invalidateQueries({ queryKey: ['providers'] });
      toast.success('Developer status updated successfully');
      setIsBulkDevModalOpen(false);
    } catch (e) {
      toast.error('Failed to update developer status');
    }
    setIsBulkDevSaving(false);
  };

  const [bulkAggId, setBulkAggId] = useState<number | ''>('');
  const [bulkState, setBulkState] = useState<Record<number, string[]>>({});
  const [isBulkSaving, setIsBulkSaving] = useState(false);

  const openBulkCurrencyModal = () => {
    requirePassword(() => {
      setBulkAggId('');
      setBulkState({});
      setAvailableCurrencies([]);
      setIsCurrencyModalOpen(true);
    });
  };

  const handleAggregatorSelect = (aggId: number) => {
    setBulkAggId(aggId);
    const agg = aggregators.find((a: any) => a.id === aggId);
    if (!agg) return setAvailableCurrencies([]);
    
    let aggCurrencies: string[] = [];
    try {
       const settings = typeof agg.api_settings === 'string' ? JSON.parse(agg.api_settings) : agg.api_settings;
       if (Array.isArray(settings)) {
          if (settings.length > 0 && settings[0].supported_currencies) {
             aggCurrencies = settings[0].supported_currencies.map((c: string) => c.toUpperCase());
          } else {
             aggCurrencies = settings.map((s: any) => s.currency?.toUpperCase()).filter(Boolean);
          }
       }
    } catch(e) {}

    // Intersect with globally active currencies from DB
    const activeDbCurrencies = allActiveCurrencies.map((c: any) => c.code.toUpperCase());
    const finalCurrencies = aggCurrencies.length > 0
      ? aggCurrencies.filter((c: string) => activeDbCurrencies.includes(c))
      : activeDbCurrencies;
    
    setAvailableCurrencies(finalCurrencies);
    
    // Init bulk state
    const aggProviders = providers.filter((p: any) => p.aggregator_id === aggId);
    const newState: Record<number, string[]> = {};
    aggProviders.forEach((p: any) => {
       newState[p.id] = p.supported_currencies || [];
    });
    setBulkState(newState);
  };


  const handleGlobalSelectAll = (checked: boolean) => {
    const aggProviders = providers.filter((p: any) => p.aggregator_id === bulkAggId);
    setBulkState(prev => {
      const newState = { ...prev };
      aggProviders.forEach((p: any) => {
        newState[p.id] = checked ? [...availableCurrencies] : [];
      });
      return newState;
    });
  };

  const handleProviderSelectAll = (providerId: number, checked: boolean) => {
    setBulkState(prev => ({
      ...prev,
      [providerId]: checked ? [...availableCurrencies] : []
    }));
  };

  const handleBulkCurrencyToggle = (providerId: number, currency: string, checked: boolean) => {
    setBulkState(prev => {
      const pCurrs = prev[providerId] || [];
      return {
        ...prev,
        [providerId]: checked ? [...pCurrs, currency] : pCurrs.filter(c => c !== currency)
      };
    });
  };

  const saveBulkCurrencies = async () => {
    if (!bulkAggId) return;
    setIsBulkSaving(true);
    try {
      const aggProviders = providers.filter((p: any) => p.aggregator_id === bulkAggId);
      const token = localStorage.getItem('token');
      
      const promises = aggProviders.map((p: any) => {
         const newCurrencies = bulkState[p.id] || [];
         // only update if changed
         const oldCurrencies = p.supported_currencies || [];
         const isChanged = newCurrencies.length !== oldCurrencies.length || !newCurrencies.every(c => oldCurrencies.includes(c));
         
         if (isChanged) {
            return apiClient.put(`/api/game-engine/admin/providers/${p.id}`, { ...p, supported_currencies: newCurrencies, _isToggle: true }, { headers: { Authorization: `Bearer ${token}` } });
         }
         return Promise.resolve();
      });
      
      await Promise.all(promises);
      queryClient.invalidateQueries({ queryKey: ['providers'] });
      toast.success('Currencies updated successfully');
      setIsCurrencyModalOpen(false);
    } catch (e) {
      toast.error('Failed to update some providers');
    }
    setIsBulkSaving(false);
  };

  // Removed old openCurrencyModal
  /*
    requirePassword(() => {
      const agg = aggregators.find((a: any) => a.id === prov.aggregator_id);
      if (!agg) return toast.error('Aggregator not found');
      
      let curr: string[] = [];
      try {
         const settings = typeof agg.api_settings === 'string' ? JSON.parse(agg.api_settings) : agg.api_settings;
         if (Array.isArray(settings)) {
            if (settings.length > 0 && settings[0].supported_currencies) {
               curr = settings[0].supported_currencies;
            } else {
               curr = settings.map((s: any) => s.currency?.toUpperCase()).filter(Boolean);
            }
         }
      } catch(e) {}
      
      if (curr.length === 0) return toast.error('No currencies defined in this Aggregator');
      
      setAvailableCurrencies(curr);
      setProviderCurrencies(prov.supported_currencies || []);
      setSelectedProvider(prov);
      setIsCurrencyModalOpen(true);
    });
  };

*/
  
  
    
  const handleToggleDeveloperStatus = (prov: Provider, checked: boolean) => {
    requirePassword(() => saveMutation.mutate({ ...prov, developer_is_enabled: checked }));
  };

  const filtered = providers.filter((p: any) => 
    p.developer_is_enabled && 
    (!searchTerm || p.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );
  const totalPages = Math.max(1, Math.ceil(filtered.length / limit));
  const paginated = filtered.slice((page - 1) * limit, page * limit);

  const columns = [
    { key: 'id', label: 'ID', width: 'w-16' },
    { key: 'aggregator_name', label: 'Aggregator' },
    { key: 'name', label: 'Name', render: (val: string, item: Provider) => (
      <span onClick={() => navigate(`/game-engine/games?provider_id=${item.id}`)} className="font-semibold text-slate-200 hover:text-indigo-400 hover:underline cursor-pointer">
        {val}
      </span>
    ) },
    { key: 'provider_code', label: 'Code' },
    { key: 'game_count', label: 'Games', render: (val: number, item: Provider) => (
      <span onClick={() => navigate(`/game-engine/games?provider_id=${item.id}`)} className="font-bold text-indigo-400 hover:text-indigo-300 hover:underline cursor-pointer">
        {val || 0}
      </span>
    ) },
    {
      key: 'developer_is_enabled', label: 'Dev Status',
      render: (val: boolean, item: Provider) => <Toggle checked={val} onChange={(c) => handleToggleDeveloperStatus(item, c)} />
    },
    {
      key: 'admin_is_enabled', label: 'Admin Status',
      render: (val: boolean, item: Provider) => <Toggle checked={val} onChange={(c) => saveMutation.mutate({ ...item, admin_is_enabled: c })} />
    },
    {
      key: 'api_is_enabled', label: 'API Status',
      render: (val: boolean) => <Badge variant={val ? "success" : "danger"}>{val ? "Active" : "Down"}</Badge>
    },
    {
      key: 'supported_currencies', label: 'Currencies',
      render: (val: string[]) => (
        <div className="flex flex-wrap gap-1 max-w-[150px]">
          {val && val.length > 0 ? val.map(c => <Badge key={c} variant="info">{c}</Badge>) : <span className="text-slate-500 text-xs">None</span>}
        </div>
      )
    },
  ];

  return (
    <>
      <StakeTable
        title="Game Providers"
        columns={columns}
        data={paginated}
        isLoading={isLoading}
        searchPlaceholder="Search providers..."
        onSearch={(q) => { setSearchTerm(q); setPage(1); }}
        page={page}
        totalPages={totalPages}
        total={filtered.length}
        limit={limit}
        onLimitChange={setLimit}
        onPageChange={setPage}
        selectable={true}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        emptyMessage="No providers found."
        inlineBottomActions={true}
        bottomActions={
          <div className="flex flex-wrap items-center gap-2.5 w-full">
            {selectedIds.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 bg-slate-800/50 p-1.5 rounded-lg border border-slate-700 w-full sm:w-auto">
                <span className="text-xs text-slate-300 font-medium whitespace-nowrap">{selectedIds.length} selected</span>
                <select
                  className="bg-slate-900 border border-slate-700 text-slate-300 text-xs rounded-md shadow-sm hover:bg-slate-800 focus:outline-none focus:border-indigo-500 py-1.5 px-2 transition-colors cursor-pointer"
                  value={selectedAssignGroupId}
                  onChange={(e) => setSelectedAssignGroupId(e.target.value ? Number(e.target.value) : '')}
                >
                  <option value="" className="bg-slate-900 text-slate-400">Group...</option>
                  {groups.map((g: any) => (
                    <option key={g.id} value={g.id} className="bg-slate-900 text-slate-200">{g.name}</option>
                  ))}
                </select>
                <Button 
                  variant="success" 
                  size="xs"
                  disabled={!selectedAssignGroupId || selectedIds.length === 0} 
                  loading={assignToGroupMutation.isPending}
                  onClick={() => assignToGroupMutation.mutate()}
                >
                  Assign to Group
                </Button>
              </div>
            )}
          </div>
        }
        actions={
          <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center w-full sm:w-auto">
            <select
              className="w-full sm:w-auto bg-slate-900 border border-slate-700 text-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-indigo-500 transition-all cursor-pointer"
              value={selectedAggregatorId}
              onChange={(e) => {
                setSelectedAggregatorId(e.target.value ? Number(e.target.value) : '');
                setPage(1);
              }}
            >
              <option value="">All Aggregators</option>
              {aggregators.map((agg: any) => (
                <option key={agg.id} value={agg.id}>{agg.name}</option>
              ))}
            </select>
            <div className="flex gap-2 w-full sm:w-auto">
              <Button className="flex-1 sm:flex-initial" variant="danger" size="sm" onClick={openBulkDevStatusModal}>Manage Dev Status</Button>
              <Button className="flex-1 sm:flex-initial" variant="primary" size="sm" onClick={() => openBulkCurrencyModal()}>Manage Currencies</Button>
            </div>
          </div>
        }
      />

      
      <Modal open={isPasswordModalOpen} onClose={() => setIsPasswordModalOpen(false)} title="Security Verification"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setIsPasswordModalOpen(false)}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={verifyPassword}>Verify & Continue</Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-400">This action requires developer authorization.</p>
          <input type="text" autoComplete="username" style={{ position: 'absolute', opacity: 0, top: '-9999px' }} />
          <FormInput type="password" label="Developer Password" value={passwordInput} onChange={(e) => setPasswordInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && verifyPassword()} autoFocus autoComplete="new-password" />
        </div>
      </Modal>

      <Modal open={isBulkDevModalOpen} onClose={() => setIsBulkDevModalOpen(false)} title="Manage Developer Status"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setIsBulkDevModalOpen(false)}>Cancel</Button>
            <Button variant="primary" size="sm" loading={isBulkDevSaving} onClick={saveBulkDevStatus} disabled={!bulkDevAggId}>Save All Changes</Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="space-y-1">
            <label className="block text-sm font-medium text-slate-300">Select Aggregator</label>
            <select
              className="w-full bg-slate-900 border border-slate-700 text-slate-200 rounded-lg p-2.5 text-sm outline-none focus:border-indigo-500 transition-all"
              value={bulkDevAggId}
              onChange={e => handleDevAggregatorSelect(Number(e.target.value))}
            >
              <option value="">-- Choose Aggregator --</option>
              {aggregators.map((a: any) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>

          {bulkDevAggId && (
            <div className="flex items-center justify-between bg-red-950/20 border border-red-500/30 p-3 rounded-lg mb-2">
              <span className="text-sm font-bold text-red-400">Enable All Providers</span>
              <label className="flex items-center cursor-pointer">
                <input type="checkbox" className="hidden" 
                  onChange={(e) => handleDevGlobalToggle(e.target.checked)} 
                  checked={
                    bulkDevProviders.length > 0 && 
                    bulkDevProviders.every((p: any) => bulkDevState[p.id])
                  }
                />
                <div className={`w-5 h-5 flex items-center justify-center rounded border transition-colors ${bulkDevProviders.length > 0 && bulkDevProviders.every((p: any) => bulkDevState[p.id]) ? 'bg-red-600 border-red-600' : 'bg-slate-950 border-slate-600'}`}>
                  {bulkDevProviders.length > 0 && bulkDevProviders.every((p: any) => bulkDevState[p.id]) && <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                </div>
              </label>
            </div>
          )}

          {bulkDevAggId && (
            <div className="mt-4 max-h-[55vh] overflow-y-auto space-y-3 pb-2">
              {bulkDevProviders.length === 0 ? (
                <div className="text-center text-slate-500 text-sm py-6">Loading providers...</div>
              ) : (
                bulkDevProviders.map((prov: any) => {
                  const isChecked = !!bulkDevState[prov.id];
                  return (
                    <div key={prov.id} className="flex items-center justify-between p-3 bg-slate-800/40 border border-slate-700/50 rounded-lg">
                      <div>
                        <h4 className="font-bold text-slate-200 text-sm">{prov.name}</h4>
                        <p className="text-slate-500 text-xs">{prov.provider_code}</p>
                      </div>
                      <Toggle checked={isChecked} onChange={(c) => handleBulkDevToggle(prov.id, c)} />
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </Modal>
      
      <Modal open={isCurrencyModalOpen} onClose={() => setIsCurrencyModalOpen(false)} title="Manage Provider Currencies"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setIsCurrencyModalOpen(false)}>Cancel</Button>
            <Button variant="primary" size="sm" loading={isBulkSaving} onClick={saveBulkCurrencies} disabled={!bulkAggId}>Save All Changes</Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="space-y-1">
            <label className="block text-sm font-medium text-slate-300">Select Aggregator</label>
            <select
              className="w-full bg-slate-900 border border-slate-700 text-slate-200 rounded-lg p-2.5 text-sm outline-none focus:border-indigo-500 transition-all"
              value={bulkAggId}
              onChange={e => handleAggregatorSelect(Number(e.target.value))}
            >
              <option value="">-- Choose Aggregator --</option>
              {aggregators.map((a: any) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          
          {bulkAggId && availableCurrencies.length === 0 && (
             <p className="text-sm text-amber-500 mt-2">No currencies defined in this Aggregator's API settings.</p>
          )}

          
          {bulkAggId && availableCurrencies.length > 0 && (
            <div className="flex items-center justify-between bg-indigo-900/30 border border-indigo-500/50 p-3 rounded-lg mb-2">
              <span className="text-sm font-bold text-indigo-300">Select All Currencies for All Providers</span>
              <label className="flex items-center cursor-pointer">
                <input type="checkbox" className="hidden" 
                  onChange={(e) => handleGlobalSelectAll(e.target.checked)} 
                  checked={
                    providers.filter((p: any) => p.aggregator_id === bulkAggId).length > 0 && 
                    providers.filter((p: any) => p.aggregator_id === bulkAggId).every((p: any) => 
                      availableCurrencies.every(c => (bulkState[p.id] || []).includes(c))
                    )
                  }
                />
                <div className={`w-5 h-5 flex items-center justify-center rounded border transition-colors ${providers.filter((p: any) => p.aggregator_id === bulkAggId).length > 0 && providers.filter((p: any) => p.aggregator_id === bulkAggId).every((p: any) => availableCurrencies.every(c => (bulkState[p.id] || []).includes(c))) ? 'bg-indigo-600 border-indigo-600' : 'bg-slate-950 border-slate-600'}`}>
                  {providers.filter((p: any) => p.aggregator_id === bulkAggId).length > 0 && providers.filter((p: any) => p.aggregator_id === bulkAggId).every((p: any) => availableCurrencies.every(c => (bulkState[p.id] || []).includes(c))) && <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                </div>
              </label>
            </div>
          )}

          {bulkAggId && availableCurrencies.length > 0 && (
            <div className="mt-4 max-h-[55vh] overflow-y-auto space-y-4 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] pb-2">
              {providers.filter((p: any) => p.aggregator_id === bulkAggId).map((prov: any) => (
                <div key={prov.id} className="p-4 bg-slate-800/40 border border-slate-700/50 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-4 border-b border-slate-700/50 pb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-indigo-600 to-purple-600 flex items-center justify-center text-white font-bold text-sm shadow-lg shadow-indigo-500/30">
                        {prov.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h4 className="font-bold text-slate-200 text-sm">{prov.name}</h4>
                        <p className="text-slate-500 text-xs tracking-wider">{prov.provider_code}</p>
                      </div>
                    </div>
                    
                    <label className="flex items-center gap-2 cursor-pointer bg-slate-900/50 px-2.5 py-1.5 rounded border border-slate-700/50 hover:bg-slate-800 transition-colors">
                      <span className="text-xs text-slate-400 font-semibold uppercase">All</span>
                      <input type="checkbox" className="hidden" 
                        onChange={(e) => handleProviderSelectAll(prov.id, e.target.checked)} 
                        checked={availableCurrencies.length > 0 && availableCurrencies.every(c => (bulkState[prov.id] || []).includes(c))}
                      />
                      <div className={`w-4 h-4 flex items-center justify-center rounded border transition-colors ${availableCurrencies.length > 0 && availableCurrencies.every(c => (bulkState[prov.id] || []).includes(c)) ? 'bg-indigo-600 border-indigo-600' : 'bg-slate-950 border-slate-600'}`}>
                        {availableCurrencies.length > 0 && availableCurrencies.every(c => (bulkState[prov.id] || []).includes(c)) && <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                      </div>
                    </label>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                    {availableCurrencies.map(currency => {
                      const isChecked = (bulkState[prov.id] || []).includes(currency);
                      return (
                        <label key={currency} className={`flex items-center justify-between px-3 py-2.5 rounded-lg border ${isChecked ? 'bg-indigo-600/20 border-indigo-500 shadow-sm shadow-indigo-500/10' : 'bg-slate-900/40 border-slate-700/60 hover:bg-slate-800 hover:border-slate-600'} cursor-pointer transition-all duration-200`}>
                          <span className={`text-xs font-bold ${isChecked ? 'text-indigo-400' : 'text-slate-400'}`}>{currency}</span>
                          <div className={`w-4 h-4 flex items-center justify-center rounded border transition-colors ${isChecked ? 'bg-indigo-600 border-indigo-600' : 'bg-slate-950 border-slate-600'}`}>
                            {isChecked && <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                          </div>
                          <input type="checkbox" className="hidden" 
                            checked={isChecked} 
                            onChange={(e) => handleBulkCurrencyToggle(prov.id, currency, e.target.checked)} 
                          />
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};
export default Providers;
